'use client';

import * as React from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { LucideIcon } from 'lucide-react';

/**
 * FormFooter - Reusable footer component for audience edit forms
 * 
 * Provides flexible footer with configurable buttons for:
 * - Single action (Save)
 * - Multiple actions (Back + Save, Cancel + Confirm)
 * - Different alignments (left, center, right, space-between)
 * 
 * @example
 * ```tsx
 * // Single save button
 * <FormFooter 
 *   buttons={[
 *     {
 *       label: 'Save',
 *       type: 'submit',
 *       variant: 'primary',
 *       disabled: !isValid,
 *       loading: isSaving
 *     }
 *   ]}
 * />
 * 
 * // Back + Confirm pattern
 * <FormFooter 
 *   align="space-between"
 *   buttons={[
 *     { label: 'Back', variant: 'outline', onClick: onBack },
 *     { label: 'Confirm', type: 'submit', variant: 'primary' }
 *   ]}
 * />
 * ```
 */

export interface FormFooterButton {
  /**
   * Button label text
   */
  label: string;

  /**
   * Optional leading icon
   */
  icon?: LucideIcon;

  /**
   * Click handler (not needed if type="submit")
   */
  onClick?: () => void;

  /**
   * Button type
   * @default "button"
   */
  type?: 'button' | 'submit' | 'reset';

  /**
   * Button visual style variant
   * @default "default"
   */
  variant?: 'default' | 'primary' | 'secondary' | 'outline' | 'ghost' | 'destructive';

  /**
   * Disabled state
   */
  disabled?: boolean;

  /**
   * Loading state (shows loading indicator)
   */
  loading?: boolean;

  /**
   * Text to show during loading
   * @default "Loading..."
   */
  loadingLabel?: string;

  /**
   * Custom CSS classes
   */
  className?: string;

  /**
   * Minimum width for the button
   */
  minWidth?: string;
}

interface FormFooterProps {
  /**
   * Array of button configurations
   */
  buttons: FormFooterButton[];

  /**
   * Footer content alignment
   * @default "right"
   */
  align?: 'left' | 'center' | 'right' | 'space-between';

  /**
   * Additional CSS classes for footer container
   */
  className?: string;
}

export const FormFooter: React.FC<FormFooterProps> = ({
  buttons,
  align = 'right',
  className = '',
}) => {
  const alignmentClass = {
    left: 'justify-start',
    center: 'justify-center',
    right: 'justify-end',
    'space-between': 'justify-between',
  }[align];

  return (
    <div className={cn('px-6 py-4 border-t border-slate-200 flex gap-3', alignmentClass, className)}>
      {buttons.map((button, idx) => {
        const isPrimary = button.variant === 'primary';
        const minWidthClass = button.minWidth || (isPrimary ? 'min-w-[120px]' : '');
        const Icon = button.icon;

        return (
          <Button
            key={idx}
            type={button.type || 'button'}
            variant={button.variant === 'primary' ? 'default' : button.variant}
            onClick={button.onClick}
            disabled={button.disabled || button.loading}
            className={cn(
              minWidthClass,
              isPrimary && 'bg-slate-900 hover:bg-slate-800 text-white',
              button.className
            )}
          >
            {button.loading ? (
              button.loadingLabel || 'Loading...'
            ) : (
              <div className="flex items-center gap-2">
                {Icon && <Icon className="h-4 w-4" />}
                <span>{button.label}</span>
              </div>
            )}
          </Button>
        );
      })}
    </div>
  );
};
